import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function InviteCodeManagement() {
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    teamId: '',
    expiresInDays: 30,
    count: 1,
    notes: ''
  });

  useEffect(() => {
    fetchCodes();
    fetchAvailableTeams();
  }, []);

  const fetchCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invite-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(response.data);
    } catch (err) {
      setError('Failed to fetch invite codes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invite-codes/available-teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableTeams(response.data);
    } catch (err) {
      console.error('Failed to fetch available teams:', err);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setGenerating(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/invite-codes/generate`,
        {
          ...formData,
          teamId: formData.teamId || null,
          createdBy: user.id
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(response.data.message);
      setFormData({ teamId: '', expiresInDays: 30, count: 1, notes: '' });
      fetchCodes();
      fetchAvailableTeams();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate invite codes');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Are you sure you want to revoke this invite code?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/invite-codes/${id}/revoke`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSuccess('Invite code revoked successfully');
      fetchCodes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke invite code');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invite code?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/invite-codes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Invite code deleted successfully');
      fetchCodes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete invite code');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setSuccess(`Code "${code}" copied to clipboard!`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const getFilteredCodes = () => {
    if (filter === 'all') return codes;
    return codes.filter(code => code.status === filter);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 dark:bg-green-900/30 text-green-800';
      case 'used': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800';
      case 'expired': return 'bg-red-100 dark:bg-red-900/30 text-red-800';
      case 'revoked': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return <div className="text-center py-8">Loading invite codes...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Invite Code Management</h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 dark:text-green-400 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Generate Form */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Generate New Invite Code(s)</h3>
        <form onSubmit={handleGenerate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assign to Team (Optional)
              </label>
              <select
                value={formData.teamId}
                onChange={(e) => setFormData({ ...formData, teamId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-nba-blue dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                <option value="">No team (Admin will assign later)</option>
                {availableTeams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Expires In (Days)
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={formData.expiresInDays}
                onChange={(e) => setFormData({ ...formData, expiresInDays: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-nba-blue dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Number of Codes
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={formData.count}
                onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-nba-blue dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes (Optional)
              </label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="e.g., For Season 2 owners"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-nba-blue dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={generating}
            className="w-full md:w-auto px-6 py-2 bg-nba-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {generating ? 'Generating...' : `Generate ${formData.count} Code(s)`}
          </button>
        </form>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <div className="flex space-x-2">
          {['all', 'active', 'used', 'expired', 'revoked'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-md capitalize ${
                filter === status
                  ? 'bg-nba-blue text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:bg-gray-600'
              }`}
            >
              {status} ({codes.filter(c => status === 'all' || c.status === status).length})
            </button>
          ))}
        </div>
      </div>

      {/* Codes Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Team
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Used By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Notes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {getFilteredCodes().length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No invite codes found
                  </td>
                </tr>
              ) : (
                getFilteredCodes().map((code) => (
                  <tr key={code.id} className="hover:bg-gray-50 dark:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono font-bold text-nba-blue">{code.code}</span>
                        <button
                          onClick={() => copyToClipboard(code.code)}
                          className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                          title="Copy to clipboard"
                        >
                          📋
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {code.team?.name || <span className="text-gray-400">Unassigned</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(code.status)}`}>
                        {code.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(code.expiresAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {code.user ? (
                        <div>
                          <div>{code.user.username}</div>
                          <div className="text-xs text-gray-400">{formatDate(code.usedAt)}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {code.notes || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex space-x-2">
                        {code.status === 'active' && (
                          <>
                            <button
                              onClick={() => handleRevoke(code.id)}
                              className="text-yellow-600 hover:text-yellow-800"
                            >
                              Revoke
                            </button>
                            <button
                              onClick={() => handleDelete(code.id)}
                              className="text-red-600 dark:text-red-400 hover:text-red-800"
                            >
                              Delete
                            </button>
                          </>
                        )}
                        {code.status !== 'used' && code.status !== 'active' && (
                          <button
                            onClick={() => handleDelete(code.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800"
                          >
                            Delete
                          </button>
                        )}
                        {code.status === 'used' && (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default InviteCodeManagement;
